import { apiClient } from '@/services/http/client'
import type {
  BusinessAccessContext,
  BusinessEntitySummary,
  BusinessWorkspace,
  ComplianceItem,
  RepresentativeGrant,
} from '@/types/businessPortal.types'
import type { BusinessPortalService } from './businessPortal.service'

type RecordValue = Record<string, unknown>

function record(value: unknown): RecordValue {
  return value && typeof value === 'object' ? value as RecordValue : {}
}

function rows(value: unknown): unknown[] {
  if (Array.isArray(value)) return value
  const source = record(value)
  return Array.isArray(source.items) ? source.items : []
}

function text(value: unknown, fallback = ''): string {
  return value === null || value === undefined ? fallback : String(value)
}

function entity(value: unknown): BusinessEntitySummary {
  const source = record(value)
  return {
    id: text(source.customer_id),
    name: text(source.business_name, 'Business entity'),
    tradeName: source.trade_name ? text(source.trade_name) : undefined,
    businessType: text(source.business_type, 'business'),
    gstin: text(source.gstin),
    pan: text(source.pan),
    tan: source.tan ? text(source.tan) : undefined,
    email: source.email ? text(source.email) : undefined,
    status: text(source.status, 'active'),
    accessKind: source.access_kind === 'representative_grant'
      ? 'representative_grant' : 'membership',
  }
}

function grant(value: unknown): RepresentativeGrant {
  const source = record(value)
  return {
    id: text(source.grant_id),
    customerType: source.customer_type === 'individual' ? 'individual' : 'business',
    customerId: text(source.customer_id),
    granteeUserId: text(source.grantee_user_id),
    grantorUserId: text(source.grantor_user_id),
    actions: Array.isArray(source.actions) ? source.actions.map(String) : [],
    status: ['pending', 'accepted', 'declined', 'revoked'].includes(text(source.status))
      ? text(source.status) as RepresentativeGrant['status'] : 'pending',
    effectiveAt: source.effective_at ? text(source.effective_at) : undefined,
    expiresAt: source.expires_at ? text(source.expires_at) : undefined,
    evidenceReference: text(source.evidence_reference),
    decisionNote: source.decision_note ? text(source.decision_note) : undefined,
    revokedAt: source.revoked_at ? text(source.revoked_at) : undefined,
  }
}

function item(value: unknown, kind: string): ComplianceItem {
  const source = record(value)
  const id = source.id ?? source.assessment_id ?? source.payment_id ?? source.reminder_id ?? source.document_id
  const period = source.assessment_period ?? source.assessment_year ?? source.period
  const title = source.title ?? source.name ?? source.return_type ?? source.itr_form ?? source.description
  const rawAmount = source.amount ?? source.total_tax_liability ?? source.tax_payable
  return {
    id: text(id, `${kind}-${Math.random()}`),
    title: text(title, kind),
    subtitle: period ? text(period) : undefined,
    status: text(source.status, 'available'),
    dueDate: source.due_date ? text(source.due_date) : undefined,
    amount: typeof rawAmount === 'number' ? rawAmount : undefined,
    currency: source.currency ? text(source.currency) : undefined,
  }
}

async function optionalRows(path: string): Promise<unknown[]> {
  try {
    return rows((await apiClient.get(path)).data)
  } catch {
    return []
  }
}

async function gstRows(path: string): Promise<{ items: unknown[]; unavailable: boolean }> {
  try {
    const data = (await apiClient.get(path)).data
    if (!Array.isArray(data) && !Array.isArray(record(data).items))
      return { items: [], unavailable: true }
    return { items: rows(data), unavailable: false }
  } catch {
    return { items: [], unavailable: true }
  }
}

export function createHttpBusinessPortalService(): BusinessPortalService {
  return {
    async getAccessContext(role): Promise<BusinessAccessContext> {
      const source = record((await apiClient.get('/customers/access/context')).data)
      return {
        role,
        entities: rows(source.business_entities).map(entity),
        activeGrants: rows(source.active_grants).map(grant),
        scopeRefreshSeconds: typeof source.customer_scope_revocation_max_seconds === 'number'
          ? source.customer_scope_revocation_max_seconds : 1800,
      }
    },

    async listGrants() {
      return rows((await apiClient.get('/customers/access/grants')).data).map(grant)
    },

    async loadWorkspace(customerId): Promise<BusinessWorkspace> {
      const encoded = encodeURIComponent(customerId)
      const [gst, incomeTax, documents, deadlines, payments] = await Promise.all([
        gstRows(`/gst-assessments/by-customer/${encoded}`),
        optionalRows(`/tax-assessments/customer/${encoded}`),
        optionalRows(`/documents/by-entity/business_customer/${encoded}`),
        optionalRows('/reminders/my-reminders'),
        optionalRows(`/payments/customer/${encoded}`),
      ])
      const scopedDeadlines = deadlines.filter((value) => {
        const source = record(value)
        return !source.entity_id || text(source.entity_id) === customerId
      })
      return {
        gst: gst.items.map((value) => item(value, 'GST return')),
        gstUnavailable: gst.unavailable,
        incomeTax: incomeTax.map((value) => item(value, 'Income-tax return')),
        tds: scopedDeadlines.filter((value) => /tds|tan/i.test(text(record(value).title)))
          .map((value) => item(value, 'TDS obligation')),
        notices: scopedDeadlines.filter((value) => /notice|demand|query/i.test(text(record(value).title)))
          .map((value) => item(value, 'Notice')),
        documents: documents.map((value) => item(value, 'Document')),
        deadlines: scopedDeadlines.map((value) => item(value, 'Deadline')),
        payments: payments.map((value) => item(value, 'Payment')),
      }
    },

    async createGrant(input) {
      const response = await apiClient.post('/customers/access/grants', {
        customer_type: 'business',
        customer_id: Number(input.customerId),
        grantee_user_id: input.granteeUserId,
        actions: input.actions,
        expires_at: input.expiresAt || null,
        evidence_reference: input.evidenceReference,
      })
      return grant(response.data)
    },

    async decideGrant(grantId, decision, note) {
      const response = await apiClient.post(
        `/customers/access/grants/${encodeURIComponent(grantId)}/decision`,
        { decision, note: note || null },
      )
      return grant(response.data)
    },

    async revokeGrant(grantId) {
      const response = await apiClient.post(
        `/customers/access/grants/${encodeURIComponent(grantId)}/revoke`,
      )
      return grant(response.data)
    },
  }
}
